import React, { useRef } from 'react';
import styled from 'styled-components';
import {
	AnimationContainer,
	AnimationImageAsset,
	AnimationImageElement,
	AnimationPositionElement,
	useAnimationContainerTimeline,
} from '../AnimationCommon';
import { AnimationElementTreetop } from '../AnimationElementTreetop';
import { AnimationElementLamps } from '../AnimationElementLamps';
import { Breakpoints } from '@client/style/Variables';
import Functions from '@client/style/Functions';
import { gsap } from 'gsap';
import { Power1 } from 'gsap';
import LongRainbowSvg from '@client/assets/svgs/animations/long-rainbow.svg';
import { useSurroundingColors } from '@client/modules/shared/UseSurroundingColors';

const StyledAnimationContainer = styled(AnimationContainer)`
	${Functions.breakpoint(Breakpoints.mobile)} {
		margin-top: -3%;
	}
`;

const RainbowContainer = styled.div`
	width: 420vw;

	svg {
		width: 100%;
		height: auto;
	}

	${Functions.breakpoint(Breakpoints.mobile)} {
		width: 188vw;
	}
`;

const MobileContainer = styled.span`
	display: block;
	${Functions.breakpoint(Breakpoints.mobile)} {
		display: none;
	}
`;
const DesktopContainer = styled.span`
	display: none;
	${Functions.breakpoint(Breakpoints.mobile)} {
		display: block;
	}
`;

const ColorWrapper = styled.div`
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	z-index: -1;
	position: absolute;
`;

const TopColor = styled.div<{ bgColor?: string }>`
	top: 0;
	left: 0;
	width: 100%;
	height: 50%;
	z-index: -1;
	background-color: ${props => props.bgColor || ''};
	position: absolute;
`;

const BottomColor = styled.div<{ bgColor?: string }>`
	bottom: 0;
	left: 0;
	width: 100%;
	height: 50%;
	z-index: -1;
	background-color: ${props => props.bgColor || ''};
	position: absolute;
`;

const Flower = () => {
	const ref = React.useRef<HTMLImageElement>(null);

	const image = new AnimationImageAsset('https://www.datocms-assets.com/80292/1687182001-kapselblomst.png', {});
	const containerTimeline = useAnimationContainerTimeline();

	React.useEffect(() => {
		const delay = 0;
		const mainDuration = 0.5;
		const prepDuration = 0.1;

		const skewTl = gsap.timeline({ repeat: -1, repeatDelay: delay });
		skewTl.fromTo(ref.current, { skewX: 1 }, { skewX: 5, duration: mainDuration, ease: 'elastic.out(1.5, 0.4)' });
		skewTl.to(ref.current, { skewX: 1, duration: prepDuration, ease: 'none' });
		skewTl.fromTo(
			ref.current,
			{ skewX: 1 },
			{ skewX: -2.5, duration: mainDuration, ease: 'elastic.out(1.5, 0.4)', delay: delay }
		);
		skewTl.to(ref.current, { skewX: 1, duration: prepDuration, ease: 'none' });

		const timeline = gsap.timeline({ repeat: -1, repeatDelay: delay });
		timeline.set(ref.current, { transformOrigin: 'bottom center' });
		timeline.fromTo(
			ref.current,
			{
				scaleY: 1,
			},
			{ scaleY: 1.05, duration: mainDuration, ease: 'elastic.out(1.5, 0.4)' },
			0
		);
		timeline.to(ref.current, { scale: 1, duration: prepDuration, ease: 'none' });
		containerTimeline.add(skewTl, 0);
		containerTimeline.add(timeline, 0);

		return () => {
			timeline.kill();
			skewTl.kill();
		};
	}, []);

	return (
		<AnimationPositionElement
			position={{
				width: '71vw',
				anchor: 'right',
				x: '11%',
				y: '18%',
				breakpoints: { mobile: { width: '24vw', anchor: 'center', x: '-57.5%', y: '30%' } },
			}}
		>
			<AnimationImageElement ref={ref} image={image} />
		</AnimationPositionElement>
	);
};

const FaceFlower = () => {
	const ref = React.useRef<HTMLImageElement>(null);

	const image = new AnimationImageAsset('https://www.datocms-assets.com/80292/1687182913-party-blomst.png', {});

	const containerTimeline = useAnimationContainerTimeline();

	React.useEffect(() => {
		const duration = 0.3;
		const timeline = gsap.timeline({ repeat: -1, repeatDelay: 0 });
		timeline.set(ref.current, { transformOrigin: 'center center -50px' });

		timeline.fromTo(
			ref.current,
			{
				rotateX: 0,
				rotateZ: 0,
				skewY: 1,
			},
			{ rotateX: -10, rotateZ: -5, skewY: '7%', x: '-=4%', y: '+=2%', duration: duration, ease: Power1.easeInOut }
		);
		timeline.to(ref.current, {
			rotateX: 0,
			rotateZ: 0,
			skewY: 1,
			x: '+=4%',
			y: '-=2%',
			duration: 0.3,
			ease: Power1.easeInOut,
		});

		containerTimeline.add(timeline, 0);

		return () => {
			timeline.kill();
		};
	}, []);
	return (
		<AnimationPositionElement
			position={{
				width: '57vw',
				anchor: 'right',
				x: '25%',
				y: '156%',
				breakpoints: { mobile: { width: '25vw', x: '-2%', y: '150%' }, ultra: { y: '143%' } },
			}}
		>
			<AnimationImageElement ref={ref} image={image} />
		</AnimationPositionElement>
	);
};

export default function IllustrationKabselblomst() {
	const moduleRef = useRef<HTMLDivElement | null>(null);
	const backgroundColors = useSurroundingColors(moduleRef);

	return (
		<StyledAnimationContainer
			height={1280}
			width={690}
			breakpoints={{ mobile: { width: 2600, height: 1850 }, ultra: { width: 4000, height: 2750 } }}
			ref={moduleRef}
		>
			<ColorWrapper>
				<TopColor bgColor={backgroundColors[0]}></TopColor>
				<BottomColor bgColor={backgroundColors[1]}></BottomColor>
			</ColorWrapper>
			<MobileContainer>
				<AnimationElementTreetop
					variant={8}
					position={{ flip: true, x: '-10%', y: '48%', width: '60vw', breakpoints: { tiny: { width: '84vw' } } }}
				></AnimationElementTreetop>
			</MobileContainer>
			<DesktopContainer>
				<AnimationElementTreetop
					variant={7}
					position={{
						x: '1%',
						y: '48%',
						anchor: 'right',
						breakpoints: {
							tiny: { width: '42vw' },
							mobile: { width: '42vw' },
							tablet: { width: '42vw' },
							laptop: { width: '42vw' },
							desktop: { width: '42vw' },
							largeDesktop: { width: '42vw' },
						},
					}}
				></AnimationElementTreetop>
				<AnimationElementTreetop
					variant={6}
					position={{
						flip: true,
						x: '-59%',
						y: '0%',
						breakpoints: {
							tiny: { width: '40vw' },
							mobile: { width: '40vw' },
							tablet: { width: '40vw' },
							laptop: { width: '40vw' },
							desktop: { width: '40vw' },
							largeDesktop: { width: '40vw' },
						},
					}}
				></AnimationElementTreetop>
				<AnimationElementTreetop
					variant={7}
					position={{
						flip: true,
						x: '-31%',
						y: '37%',
						breakpoints: {
							tiny: { width: '113vw' },
							mobile: { width: '45vw', x: '-1%' },
							tablet: { width: '45vw' },
							laptop: { width: '45vw' },
							desktop: { width: '45vw' },
							largeDesktop: { width: '45vw' },
						},
					}}
				></AnimationElementTreetop>
				<AnimationElementTreetop
					variant={6}
					position={{
						x: '50.5%',
						y: '8%',
						anchor: 'right',
						breakpoints: {
							tiny: { width: '40vw' },
							mobile: { width: '40vw' },
							tablet: { width: '40vw' },
							laptop: { width: '40vw' },
							desktop: { width: '40vw' },
							largeDesktop: { width: '40vw' },
						},
					}}
				></AnimationElementTreetop>
			</DesktopContainer>
			<AnimationPositionElement
				position={{ y: '45%', angle: -7, x: '-31%', breakpoints: { mobile: { x: '-26%', angle: -7, y: '21%' } } }}
			>
				<RainbowContainer>
					<LongRainbowSvg />
				</RainbowContainer>
			</AnimationPositionElement>
			<DesktopContainer>
				<AnimationElementTreetop
					variant={6}
					position={{
						x: '64%',
						y: '74%',
						breakpoints: {
							tiny: { width: '40vw' },
							mobile: { width: '40vw' },
							tablet: { width: '40vw' },
							laptop: { width: '40vw' },
							desktop: { width: '40vw' },
							largeDesktop: { width: '40vw' },
						},
					}}
				></AnimationElementTreetop>
				<AnimationElementTreetop
					variant={3}
					position={{
						x: '-50%',
						y: '74%',
						anchor: 'right',
						breakpoints: {
							tiny: { width: '113vw' },
							mobile: { width: '38vw' },
							tablet: { width: '38vw' },
							laptop: { width: '38vw' },
							desktop: { width: '38vw' },
							largeDesktop: { width: '38vw' },
						},
					}}
				></AnimationElementTreetop>
			</DesktopContainer>
			<Flower />

			<MobileContainer>
				<AnimationElementTreetop
					variant={3}
					position={{ y: '75%', x: '40%', anchor: 'right', breakpoints: { tiny: { width: '113vw' } } }}
				></AnimationElementTreetop>
				<AnimationElementTreetop
					variant={7}
					position={{
						flip: true,
						x: '-31%',
						y: '103%',
						breakpoints: {
							tiny: { width: '113vw' },
							mobile: { width: '45vw', x: '-1%' },
							tablet: { width: '45vw' },
							laptop: { width: '45vw' },
							desktop: { width: '45vw' },
							largeDesktop: { width: '45vw' },
						},
					}}
				></AnimationElementTreetop>
			</MobileContainer>
			<AnimationElementLamps
				variant={6}
				position={{
					scale: 0.55,
					x: '-23%',
					y: '100%',
					breakpoints: { mobile: { scale: 0.4, y: '47%', x: '-32%' }, ultra: { y: '75%' } },
				}}
			></AnimationElementLamps>
			<AnimationElementLamps
				variant={7}
				position={{
					scale: 0.9,
					x: '-10%',
					y: '175%',
					angle: 0,
					breakpoints: { mobile: { scale: 0.65, y: '95%', x: '-21%' }, ultra: { y: '142%' } },
				}}
			></AnimationElementLamps>
			<DesktopContainer>
				<AnimationElementTreetop
					variant={3}
					position={{
						y: '112%',
						x: '42.5%',
						breakpoints: {
							tiny: { width: '38vw' },
							mobile: { width: '38vw' },
							tablet: { width: '38vw' },
							laptop: { width: '38vw' },
							desktop: { width: '38vw' },
							largeDesktop: { width: '38vw' },
						},
					}}
				></AnimationElementTreetop>
				<AnimationElementTreetop
					variant={8}
					position={{
						flip: true,
						x: '-1%',
						y: '32%',
						breakpoints: {
							tiny: { width: '113vw' },
							mobile: { width: '45vw', x: '-1%' },
							tablet: { width: '45vw' },
							laptop: { width: '45vw' },
							desktop: { width: '45vw' },
							largeDesktop: { width: '45vw' },
							ultra: { width: '45vw', y: '28%' },
						},
					}}
				></AnimationElementTreetop>
				<AnimationElementTreetop
					variant={8}
					position={{
						x: '6%',
						y: '42%',
						anchor: 'right',
						breakpoints: {
							tiny: { width: '113vw' },
							mobile: { width: '45vw' },
							tablet: { width: '45vw' },
							laptop: { width: '45vw' },
							desktop: { width: '45vw' },
							largeDesktop: { width: '45vw' },
						},
					}}
				></AnimationElementTreetop>
				<AnimationElementTreetop
					variant={8}
					position={{
						x: '1%',
						y: '50%',
						anchor: 'right',
						breakpoints: {
							tiny: { width: '113vw' },
							mobile: { width: '31vw' },
							tablet: { width: '31vw' },
							laptop: { width: '31vw' },
							desktop: { width: '31vw' },
							largeDesktop: { width: '31vw', y: '55%' },
						},
					}}
				></AnimationElementTreetop>
			</DesktopContainer>

			<MobileContainer>
				<AnimationElementTreetop
					variant={6}
					position={{ flip: true, x: '-57%', y: '125%', breakpoints: { tiny: { width: '100vw' } } }}
				></AnimationElementTreetop>
			</MobileContainer>
			<FaceFlower />
		</StyledAnimationContainer>
	);
}
